import * as React from 'react';
import { Field, chakra, type HTMLChakraProps } from '@chakra-ui/react';

/**
 * Project FormControl.
 *
 * NB-shaped facade over Chakra v3's `Field` compositional API (Chakra v3
 * renamed FormControl -> Field). Consumers keep using `<FormControl>` +
 * `FormControl.Label` / `FormControl.HelperText` /
 * `FormControl.ErrorMessage`.
 *
 * NB props map as: `isRequired` -> `required`, `isInvalid` -> `invalid`,
 * `isDisabled` -> `disabled`. Migrate those at the call site.
 *
 * `FormControl.Label` is intentionally a plain styled `<label>` (not
 * Chakra's `Field.Label`) so it can be used standalone the way NativeBase
 * allowed — several call sites render the label outside `FormControl`.
 * When nested under a `required` FormControl, a red `*` is appended via
 * our own context (NB `isRequired` parity).
 */

type RootProps = React.ComponentProps<typeof Field.Root> & {
  /** @deprecated Use `required` — kept for leftover NB call sites. */
  isRequired?: boolean;
  /** @deprecated Use `disabled` — kept for leftover NB call sites. */
  isDisabled?: boolean;
  /** @deprecated Use `invalid` — kept for leftover NB call sites. */
  isInvalid?: boolean;
};

type LabelProps = HTMLChakraProps<'label'>;

const RequiredContext = React.createContext(false);

const FormControlRoot = React.forwardRef<HTMLDivElement, RootProps>(
  (
    { required, isRequired, disabled, isDisabled, invalid, isInvalid, ...props },
    ref
  ) => {
    const isFieldRequired = Boolean(required ?? isRequired);
    return (
      <RequiredContext.Provider value={isFieldRequired}>
        <Field.Root
          ref={ref}
          required={isFieldRequired}
          disabled={disabled ?? isDisabled}
          invalid={invalid ?? isInvalid}
          gap={0}
          w="100%"
          {...props}
        />
      </RequiredContext.Provider>
    );
  }
);
FormControlRoot.displayName = 'FormControl';

// Matches the old NB FormControlLabel baseStyle, plus the auto `*` that
// NB rendered when the parent FormControl was `isRequired`.
const Label = React.forwardRef<HTMLLabelElement, LabelProps>(
  ({ children, ...rest }, ref) => {
    const required = React.useContext(RequiredContext);
    return (
      <chakra.label
        ref={ref}
        display="block"
        paddingBottom="6px"
        color="secondary.500"
        fontSize="sm"
        fontWeight="normal"
        {...rest}
      >
        {children}
        {required ? (
          <chakra.span
            aria-hidden="true"
            color="red.500"
            marginLeft="0px"
            lineHeight="1"
          >
            *
          </chakra.span>
        ) : null}
      </chakra.label>
    );
  }
);
Label.displayName = 'FormControl.Label';

export const FormControl = Object.assign(FormControlRoot, {
  Label,
  HelperText: Field.HelperText,
  ErrorMessage: Field.ErrorText,
});
